import { useEffect, useRef, useState } from 'react';

import { AlertCircleIcon, CheckCircleIcon, XIcon } from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { mcpEndpoints } from '@/api/endpoints';
import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ToggleButton } from '@/components/ui/custom/ToggleButton';
import { URLInput } from '@/components/ui/custom/URLInput/URLInput';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { SpinnerIcon } from '@/components/ui/spinner';
import { selectBrainId } from '@/redux/session/selectors';
import { resolveAvailableName } from '@/util/checkNames';

import { HeaderRow } from './HeaderRow';
import { useHeaderVisibility } from './hooks/useHeaderVisibility';
import useMcpServers, { type McpConfig } from './hooks/useMcpServers';
import { buildHeadersRecord, toMcpTool } from './utils';

type McpServerForm = {
  serverUrl: string;
  name: string;
  clientId: string;
  clientSecret: string;
  authorizationUrl: string;
  tokenUrl: string;
  customHeaders: Array<{ key: string; value: string }>;
};

/**
 * Extract a display name from MCP server URL
 * e.g., "mcp.supabase.com" → "supabase", "api.hubspot.com" → "hubspot"
 */
function extractNameFromUrl(url: string): string {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    const hostname = urlObj.hostname;
    const parts = hostname.split('.');
    // For domains like "mcp.supabase.com" or "api.hubspot.com", get the second part
    // For "supabase.com", get the first part
    if (parts.length >= 2) {
      const name = parts.length > 2 ? parts[parts.length - 2] : parts[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return hostname;
  } catch {
    return '';
  }
}

export function AddMcpServerPanel() {
  const { t } = useTranslation();
  const onClose = useResponsiveSidebarClose();
  const brainId = useSelector(selectBrainId);
  const {
    discoverMutation,
    oauthStartMutation,
    oauthExchangeMutation,
    registerMutation,
    testMutation,
    createMcpServer,
    createStatus,
  } = useMcpServers(brainId ?? undefined);

  const savedStateRef = useRef<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [headerAuthCompleted, setHeaderAuthCompleted] = useState(false);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());
  const [registeredClient, setRegisteredClient] = useState<{
    clientId: string;
    clientSecret?: string;
  } | null>(null);
  const [registrationFailed, setRegistrationFailed] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [sseError, setSseError] = useState(false);

  const form = useForm<McpServerForm>({
    defaultValues: {
      serverUrl: '',
      name: '',
      clientId: '',
      clientSecret: '',
      authorizationUrl: '',
      tokenUrl: '',
      customHeaders: [{ key: '', value: '' }],
    },
  });

  const { control, setValue, getValues } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'customHeaders' });
  const watchedHeaders = useWatch({ control, name: 'customHeaders' });
  // Only whether it is filled in, so typing the id does not re-render the panel
  const hasClientId = useWatch({ control, name: 'clientId', compute: Boolean });
  const headerVisibility = useHeaderVisibility();
  const lastKeyRef = useRef<HTMLInputElement>(null);

  const callbackUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/www/mcp/oauth/callback` : '';

  // Update name when discovery returns
  useEffect(() => {
    if (discoverMutation.data?.name) {
      setValue('name', discoverMutation.data.name);
    }
  }, [discoverMutation.data?.name, setValue]);

  // Auto-register when discovery returns registrationEndpoint (RFC7591 Dynamic Client Registration)
  useEffect(() => {
    const discovery = discoverMutation.data;
    if (
      discovery?.auth.mode === 'oauth' &&
      discovery.auth.registrationEndpoint &&
      !registeredClient &&
      !registerMutation.isPending &&
      !registrationFailed
    ) {
      registerMutation.mutate({
        registrationEndpoint: discovery.auth.registrationEndpoint,
        redirectUri: callbackUrl,
        clientName: discovery.name || extractNameFromUrl(getValues('serverUrl')),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverMutation.data?.auth.registrationEndpoint]);

  // Handle registration result
  useEffect(() => {
    if (registerMutation.isSuccess) {
      if (registerMutation.data.success && registerMutation.data.clientId) {
        setRegisteredClient({
          clientId: registerMutation.data.clientId,
          clientSecret: registerMutation.data.clientSecret,
        });
      } else {
        setRegistrationFailed(true);
      }
    }
    if (registerMutation.isError) {
      setRegistrationFailed(true);
    }
  }, [registerMutation.isSuccess, registerMutation.isError, registerMutation.data]);

  // Listen for OAuth callback messages
  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data?.type !== 'mcp-oauth-callback') {
        return;
      }

      const { code, state, error, errorDescription } = event.data;

      if (error) {
        console.error('OAuth error:', error, errorDescription);
        return;
      }

      if (code && state === savedStateRef.current) {
        try {
          const result = await oauthExchangeMutation.mutateAsync({ code, state });
          if (result.success && result.accessToken) {
            setAccessToken(result.accessToken);
            // OAuth Authorization overrides any custom header with the same key
            const customHeaders = buildHeadersRecord(getValues('customHeaders'));
            testMutation.mutate({
              url: getValues('serverUrl'),
              headers: { ...customHeaders, Authorization: `Bearer ${result.accessToken}` },
            });
          }
        } catch (err) {
          console.error('Token exchange failed:', err);
        }
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [oauthExchangeMutation, testMutation, getValues]);

  // Initialize all tools as selected when test succeeds
  useEffect(() => {
    if (testMutation.isSuccess && testMutation.data.tools) {
      setSelectedTools(new Set(testMutation.data.tools.map((t) => t.name)));
      // Mark header auth as completed if that was the auth mode
      if (discoverMutation.data?.auth.mode === 'header') {
        setHeaderAuthCompleted(true);
      }
    }
  }, [testMutation.isSuccess, testMutation.data?.tools, discoverMutation.data?.auth.mode]);

  // Clear OAuth fields on test error to allow retry; preserve customHeaders so the user can fix and retry.
  useEffect(() => {
    if (testMutation.isError) {
      setValue('clientId', '');
      setValue('clientSecret', '');
      setAccessToken(null);
      setHeaderAuthCompleted(false);
    }
  }, [testMutation.isError, setValue]);

  // Auto-call /test for auth.mode === 'none'
  useEffect(() => {
    if (discoverMutation.isSuccess && discoverMutation.data.auth.mode === 'none') {
      testMutation.mutate({ url: getValues('serverUrl') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverMutation.isSuccess, discoverMutation.data?.auth.mode]);

  // Close panel on successful save
  useEffect(() => {
    if (createStatus === 'success') {
      onClose();
    }
  }, [createStatus, onClose]);

  const handleSelectAll = () => {
    if (testMutation.data?.tools) {
      setSelectedTools(new Set(testMutation.data.tools.map((t) => t.name)));
    }
  };

  const handleSelectNone = () => {
    setSelectedTools(new Set());
  };

  const toggleTool = (toolName: string) => {
    setSelectedTools((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) {
        next.delete(toolName);
      } else {
        next.add(toolName);
      }
      return next;
    });
  };

  const handleUrlBlur = () => {
    const url = getValues('serverUrl')?.trim() ?? '';

    if (url) {
      // SSE transport is deprecated - block explicit /sse endpoints (with or without trailing slash)
      if (/\/sse\/?$/i.test(url)) {
        setSseError(true);
        return;
      }
      setSseError(false);

      // Set name from URL (will be overridden if discovery returns a name)
      const nameFromUrl = extractNameFromUrl(url);
      if (nameFromUrl) {
        setValue('name', nameFromUrl);
      }
      discoverMutation.mutate(url);
    }
  };

  const handleOAuthConnect = async () => {
    const discovery = discoverMutation.data;
    if (!discovery || discovery.auth.mode !== 'oauth') {
      return;
    }

    const { authorizationEndpoint, tokenEndpoint, scopesSuggested } = discovery.auth;
    const { authorizationUrl, tokenUrl } = getValues();

    // Use registered client credentials or manual input
    const clientId = registeredClient?.clientId || getValues('clientId');
    const clientSecret = registeredClient?.clientSecret || getValues('clientSecret');

    if (!clientId) {
      return;
    }

    // Use custom authorization URL if provided, otherwise use discovered endpoint
    let finalAuthEndpoint = authorizationEndpoint;
    if (authorizationUrl?.trim()) {
      try {
        const parsed = new URL(authorizationUrl.trim());
        finalAuthEndpoint = `${parsed.origin}${parsed.pathname}`;
      } catch {
        // Invalid URL, fall back to discovered
      }
    }

    // Use custom token URL if provided, otherwise use discovered endpoint
    let finalTokenEndpoint = tokenEndpoint;
    if (tokenUrl?.trim()) {
      finalTokenEndpoint = tokenUrl.trim();
    }

    if (!finalAuthEndpoint || !finalTokenEndpoint) {
      return;
    }

    try {
      const result = await oauthStartMutation.mutateAsync({
        mcpUrl: getValues('serverUrl'),
        authorizationEndpoint: finalAuthEndpoint,
        tokenEndpoint: finalTokenEndpoint,
        scopes: scopesSuggested?.join(' '),
        clientId,
        clientSecret: clientSecret || undefined,
        clientOrigin: window.location.origin,
        resourceUrl: discovery.auth.resourceUrl,
      });

      savedStateRef.current = result.state;
      window.open(result.authUrl, 'mcp-oauth', 'width=600,height=700');
    } catch (err) {
      console.error('OAuth start failed:', err);
    }
  };

  const handleHeaderConnect = () => {
    const headers = buildHeadersRecord(getValues('customHeaders'));
    testMutation.mutate({ url: getValues('serverUrl'), headers });
  };

  const addHeader = () => {
    append({ key: '', value: '' });
    requestAnimationFrame(() => lastKeyRef.current?.focus());
  };

  const removeHeader = (index: number) => {
    remove(index);
    headerVisibility.shiftAfterRemove(index);
  };

  const handleAddServer = async () => {
    if (!testMutation.data?.tools || !brainId) {
      return;
    }

    const name = getValues('name').trim();

    let resolvedName: string;
    try {
      const endpoint = mcpEndpoints.checkName(brainId, name);
      resolvedName = await resolveAvailableName(endpoint, name);
      setNameError(null);
    } catch {
      setNameError(t('validation.error_checking_name'));
      return;
    }

    const selectedMcpTools = testMutation.data.tools
      .filter((t) => selectedTools.has(t.name))
      .map(toMcpTool);

    const authMode = discoverMutation.data?.auth.mode;
    const authData = discoverMutation.data?.auth;

    // Build config based on auth mode
    let config: McpConfig = {};

    const customHeaders = buildHeadersRecord(getValues('customHeaders'));
    const hasCustomHeaders = Object.keys(customHeaders).length > 0;

    if (authMode === 'oauth' && accessToken) {
      const { authorizationUrl, tokenUrl } = getValues();
      const finalAuthEndpoint = authorizationUrl?.trim() || authData?.authorizationEndpoint;
      const finalTokenEndpoint = tokenUrl?.trim() || authData?.tokenEndpoint;

      if (finalAuthEndpoint && finalTokenEndpoint) {
        config = {
          auth_type: 'oauth' as const,
          client_id: registeredClient?.clientId || getValues('clientId'),
          client_secret: registeredClient?.clientSecret || getValues('clientSecret') || undefined,
          authorization_endpoint: finalAuthEndpoint,
          token_endpoint: finalTokenEndpoint,
          scopes: authData?.scopesSuggested?.join(' '),
          access_token: accessToken,
          ...(hasCustomHeaders ? { headers: customHeaders } : {}),
        };
      }
    } else if (hasCustomHeaders) {
      config = { headers: customHeaders };
    }

    createMcpServer({
      name: resolvedName,
      url: getValues('serverUrl'),
      tools: selectedMcpTools,
      is_active: true,
      config,
    });
  };

  const showOAuthForm =
    discoverMutation.isSuccess && discoverMutation.data.auth.mode === 'oauth' && !accessToken;
  const showHeaderForm =
    discoverMutation.isSuccess &&
    discoverMutation.data.auth.mode === 'header' &&
    !headerAuthCompleted;
  const showTools = testMutation.isSuccess && testMutation.data.tools;

  // Determine if manual credentials are needed (no dynamic registration, registration failed, or missing endpoints)
  const needsManualCredentials =
    discoverMutation.data?.auth.mode === 'oauth' &&
    (!discoverMutation.data?.auth.registrationEndpoint ||
      registrationFailed ||
      !discoverMutation.data?.auth.authorizationEndpoint ||
      !discoverMutation.data?.auth.tokenEndpoint);

  // Check if OAuth connect is ready
  const canOAuthConnect = registeredClient?.clientId || hasClientId;

  const customHeadersSection = (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-foreground">{t('mcp.custom_headers')}</span>
      {fields.map((field, index) => (
        <HeaderRow
          key={field.id}
          headerKey={watchedHeaders?.[index]?.key ?? ''}
          headerValue={watchedHeaders?.[index]?.value ?? ''}
          isVisible={headerVisibility.visibleHeaders.has(index)}
          isLast={index === fields.length - 1}
          onKeyChange={(val) => setValue(`customHeaders.${index}.key`, val)}
          onValueChange={(val) => setValue(`customHeaders.${index}.value`, val)}
          onToggleVisibility={() => headerVisibility.toggle(index)}
          onAdd={addHeader}
          onRemove={() => removeHeader(index)}
          keyRef={index === fields.length - 1 ? lastKeyRef : undefined}
        />
      ))}
    </div>
  );

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{t('mcp.add_mcp_server')}</h2>
        <Button variant="ghost-neutral" size="icon-sm" onClick={onClose}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>
      <Form {...form}>
        <SidebarWrapperForm autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <SidebarWrapperContent className="flex flex-col px-1">
            <FormField
              control={control}
              name="serverUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('mcp.server_url')}</FormLabel>
                  <FormControl>
                    <URLInput
                      {...field}
                      autoFocus
                      autoComplete="off"
                      protocol="https://"
                      onBlur={() => {
                        field.onBlur();
                        handleUrlBlur();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Discovery result */}
            {discoverMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <SpinnerIcon className="size-4 text-foreground" />
                <span>{t('mcp.discovering')}</span>
              </div>
            )}

            {discoverMutation.isSuccess &&
              !showOAuthForm &&
              !showTools &&
              !testMutation.isPending && (
                <div
                  className={`flex items-center gap-2 text-sm ${
                    discoverMutation.data.auth.mode === 'none' ? 'text-success' : 'text-warning'
                  }`}
                >
                  {discoverMutation.data.auth.mode === 'none' ? (
                    <CheckCircleIcon className="size-4" />
                  ) : (
                    <AlertCircleIcon className="size-4" />
                  )}
                  <span>{t(`mcp.auth_${discoverMutation.data.auth.mode}`)}</span>
                </div>
              )}

            {discoverMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircleIcon className="size-4" />
                <span>{t('mcp.discovery_error')}</span>
              </div>
            )}

            {sseError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircleIcon className="size-4" />
                <span>{t('mcp.sse_deprecated')}</span>
              </div>
            )}

            {/* OAuth Form */}
            {showOAuthForm && (
              <>
                <FormField
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('mcp.name')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete="off"
                          onChange={(e) => {
                            field.onChange(e);
                            setNameError(null);
                          }}
                        />
                      </FormControl>
                      {nameError && <p className="text-sm text-destructive">{nameError}</p>}
                    </FormItem>
                  )}
                />

                {/* Registration status (when dynamic registration is available) */}
                {discoverMutation.data?.auth.registrationEndpoint && (
                  <div className="flex items-center gap-2 text-sm">
                    {registerMutation.isPending && (
                      <>
                        <SpinnerIcon className="size-4 text-foreground" />
                        <span className="text-muted-foreground">{t('mcp.registering_client')}</span>
                      </>
                    )}
                    {registeredClient && (
                      <>
                        <CheckCircleIcon className="size-4 text-success" />
                        <span className="text-success">{t('mcp.client_registered')}</span>
                      </>
                    )}
                    {registrationFailed && (
                      <>
                        <AlertCircleIcon className="size-4 text-destructive" />
                        <span className="text-destructive">{t('mcp.registration_failed')}</span>
                      </>
                    )}
                  </div>
                )}

                {/* Manual credentials fields (only if no dynamic registration or registration failed) */}
                {needsManualCredentials && (
                  <>
                    <FormItem>
                      <FormLabel>{t('mcp.callback_url')}</FormLabel>
                      <InputGroup>
                        <InputGroupInput value={callbackUrl} readOnly />
                        <InputGroupAddon align="inline-end">
                          <CopyButton data={callbackUrl} hideIcon={false} size="sm" />
                        </InputGroupAddon>
                      </InputGroup>
                      <p className="text-xs text-muted-foreground">{t('mcp.callback_url_hint')}</p>
                    </FormItem>

                    <FormField
                      control={control}
                      name="clientId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('mcp.client_id')}</FormLabel>
                          <FormControl>
                            <Input {...field} autoComplete="off" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="clientSecret"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('mcp.client_secret')}</FormLabel>
                          <FormControl>
                            <Input {...field} type="password" autoComplete="new-password" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="authorizationUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('mcp.authorization_url')}
                            {discoverMutation.data?.auth.authorizationEndpoint && (
                              <span className="ml-1 text-muted-foreground">
                                ({t('mcp.optional')})
                              </span>
                            )}
                          </FormLabel>
                          <FormControl>
                            <URLInput {...field} />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">
                            {t('mcp.authorization_url_hint')}
                          </p>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="tokenUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('mcp.token_url')}
                            {discoverMutation.data?.auth.tokenEndpoint && (
                              <span className="ml-1 text-muted-foreground">
                                ({t('mcp.optional')})
                              </span>
                            )}
                          </FormLabel>
                          <FormControl>
                            <URLInput {...field} />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">{t('mcp.token_url_hint')}</p>
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {customHeadersSection}

                <Button
                  type="button"
                  onClick={handleOAuthConnect}
                  disabled={!canOAuthConnect}
                  isLoading={oauthStartMutation.isPending || oauthExchangeMutation.isPending}
                  className="mb-4"
                >
                  {t('mcp.connect')}
                </Button>

                {oauthExchangeMutation.isError && (
                  <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircleIcon className="size-4" />
                    <span>{t('mcp.oauth_error')}</span>
                  </div>
                )}
              </>
            )}

            {/* Header Auth Form */}
            {showHeaderForm && (
              <>
                <FormField
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('mcp.name')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete="off"
                          onChange={(e) => {
                            field.onChange(e);
                            setNameError(null);
                          }}
                        />
                      </FormControl>
                      {nameError && <p className="text-sm text-destructive">{nameError}</p>}
                    </FormItem>
                  )}
                />

                {customHeadersSection}

                <div className="flex items-center justify-end gap-2">
                  <Button type="button" variant="ghost-neutral" onClick={onClose}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleHeaderConnect}
                    disabled={
                      !watchedHeaders?.some((h) => h.key?.trim() && h.value?.trim()) ||
                      testMutation.isPending
                    }
                  >
                    {t('mcp.connect')}
                  </Button>
                </div>
              </>
            )}

            {/* Test loading */}
            {testMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <SpinnerIcon className="size-4 text-foreground" />
                <span>{t('mcp.loading_tools')}</span>
              </div>
            )}

            {/* Tools display */}
            {showTools && (
              <>
                {/* Success Alert */}
                <Alert variant="success">
                  <CheckCircleIcon className="size-4 text-success" />
                  <AlertTitle>{t('mcp.auth_completed')}</AlertTitle>
                  <AlertDescription>{t('mcp.auth_completed_desc')}</AlertDescription>
                </Alert>

                {/* Name field */}
                <FormField
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('mcp.name')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          autoComplete="off"
                          onChange={(e) => {
                            field.onChange(e);
                            setNameError(null);
                          }}
                        />
                      </FormControl>
                      {nameError && <p className="text-sm text-destructive">{nameError}</p>}
                    </FormItem>
                  )}
                />

                {customHeadersSection}

                {/* Selected tools header with None/All */}
                <div className="flex items-center gap-4 px-1 pt-2">
                  <span className="grow text-sm font-medium text-foreground">
                    {t('mcp.selected_tools', { 0: selectedTools.size })}
                  </span>
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <button type="button" className="text-primary" onClick={handleSelectNone}>
                      {t('common.none')}
                    </button>
                    <span className="text-muted-foreground">·</span>
                    <button type="button" className="text-primary" onClick={handleSelectAll}>
                      {t('common.all')}
                    </button>
                  </div>
                </div>

                {/* Tool chips */}
                <div className="flex flex-wrap gap-2">
                  {testMutation.data.tools.map((tool) => {
                    const isSelected = selectedTools.has(tool.name);
                    return (
                      <InfoTooltip
                        key={tool.name}
                        variant="rich"
                        size="auto"
                        content={
                          <div className="flex max-w-70 min-w-45 flex-col">
                            <div className="px-3 py-2">
                              <p className="text-xs leading-4 font-semibold">{tool.name}</p>
                            </div>
                            {tool.description && (
                              <div className="border-t-[0.5px] border-t-muted/40 px-3 pt-2.5 pb-3">
                                <p className="line-clamp-4 text-xs leading-5">{tool.description}</p>
                              </div>
                            )}
                          </div>
                        }
                        side="top"
                      >
                        <ToggleButton pressed={isSelected} onClick={() => toggleTool(tool.name)}>
                          {tool.name}
                        </ToggleButton>
                      </InfoTooltip>
                    );
                  })}
                </div>
              </>
            )}

            {testMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircleIcon className="size-4" />
                <span>{testMutation.error?.message || t('mcp.test_error')}</span>
              </div>
            )}
          </SidebarWrapperContent>

          {showTools && (
            <SidebarWrapperFooter className="z-15">
              <Button
                disabled={selectedTools.size === 0 || !!nameError}
                isLoading={createStatus === 'pending'}
                onClick={handleAddServer}
              >
                {t('common.add')}
              </Button>
              <Button variant="ghost-neutral" onClick={onClose}>
                {t('common.cancel')}
              </Button>
            </SidebarWrapperFooter>
          )}
        </SidebarWrapperForm>
      </Form>
    </SidebarWrapper>
  );
}
